// src/components/timeBoxes/TimeBoxDetail/index.tsx
// TimeBox 詳情主組件 - 使用 Local-first 樂觀更新和 Inline Editing

import React from 'react';
import { ArrowLeft } from '../../../../components/common/icons';
import ResponsiveModal from '../../../../components/common/ResponsiveModal';
import { useToast } from '../../../../contexts/ToastContext';
import { useConfirmDialog } from '../../../../contexts/ConfirmDialogContext';
import { useTimeBoxDetail } from '../../hooks/useTimeBoxDetail';
import { useScheduleRecommendations } from '../../../../hooks/useScheduleRecommendations';
import { getCurrentUserId } from '../../../../services/auth';
import { TimeBoxDetailContent } from './TimeBoxDetailContent';
import { TimeBoxDetailMetadata } from './TimeBoxDetailMetadata';
import { TimeBoxDetailActions } from './TimeBoxDetailActions';
import { TimeRecommendation } from '../../../../types/scheduleRecommendation';
import { TimeBox } from '../../../../types/timeBox';
import { Task } from '../../../../types/task';
import { logger } from '../../../../utils/logger';

// ============================================
// Props 接口
// ============================================

interface TimeBoxDetailProps {
  isOpen: boolean;
  timeBoxId: string | null;
  onClose: () => void;
  onRefresh?: () => void;
  mode?: 'create' | 'edit';
  onSwitchToEvent?: (timeBox: TimeBox) => void;
}

// ============================================
// 主組件
// ============================================

const TimeBoxDetail: React.FC<TimeBoxDetailProps> = ({
  isOpen,
  timeBoxId,
  onClose,
  onRefresh,
  mode = 'edit',
  onSwitchToEvent
}) => {
  const { showSuccess, showError } = useToast();
  const { confirmDelete } = useConfirmDialog();

  // 使用自定義 Hook
  const {
    timeBox,
    isLoading,
    error,
    isSubmitting,
    updateTitle,
    updateNotes,
    updateStartTime,
    updateEndTime,
    updateCategory,
    updateLocation,
    updateTaskIds,
    deleteTimeBox
  } = useTimeBoxDetail({
    timeBoxId,
    isOpen,
    onClose,
    onRefresh,
    showSuccess,
    showError,
    mode
  });

  // AI 排程建議（條件化執行 - 僅當有關聯任務時）
  const userId = getCurrentUserId();
  const shouldFetchRecommendations = !!(timeBox && timeBox.taskId);

  const {
    recommendations: aiRecommendations,
    isLoading: isLoadingRecommendations,
    error: recommendationsError
  } = useScheduleRecommendations(
    shouldFetchRecommendations && timeBox ? { id: timeBox.taskId!, title: timeBox.title } as Task : null,
    userId
  );

  // 獲取關聯任務詳情
  const [relatedTasks, setRelatedTasks] = React.useState<Task[]>([]);
  const [isLoadingRelatedTasks, setIsLoadingRelatedTasks] = React.useState(false);

  React.useEffect(() => {
    const fetchRelatedTasks = async () => {
      if (!timeBox?.taskIds || timeBox.taskIds.length === 0) {
        setRelatedTasks([]);
        return;
      }

      setIsLoadingRelatedTasks(true);
      try {
        // 動態引入 taskService 以避免循環依賴
        const { taskService } = await import('../../../task');

        // Local-first 策略：優先從 IndexedDB 讀取
        const tasks = await taskService.getTasksByIdsFromCache(
          timeBox.taskIds,
          userId,
          {
            // 快取命中時立即顯示
            onCacheHit: (cachedTasks) => {
              setRelatedTasks(cachedTasks);
              setIsLoadingRelatedTasks(false);
            },
            // 背景同步最新資料
            syncInBackground: true
          }
        );

        // 更新為最終結果（包含 Firestore 新獲取的任務）
        setRelatedTasks(tasks);
      } catch (error) {
        logger.error('TimeBoxDetail', 'Failed to fetch related tasks', error);
      } finally {
        setIsLoadingRelatedTasks(false);
      }
    };

    if (timeBox?.taskIds) {
      fetchRelatedTasks();
    } else {
      setRelatedTasks([]);
    }
  }, [timeBox?.taskIds, userId]);

  // ============================================
  // 事件處理
  // ============================================

  const handleDelete = async () => {
    if (!timeBox) return;

    const confirmed = await confirmDelete(timeBox.title || 'this time box');

    if (confirmed) {
      await deleteTimeBox();
    }
  };

  const handleSwitchToEvent = () => {
    if (!timeBox || !onSwitchToEvent) return;
    onSwitchToEvent(timeBox);
  };

  const handleSelectRecommendation = (recommendation: TimeRecommendation) => {
    if (!timeBox) return;

    // 更新開始和結束時間
    updateStartTime(recommendation.startTime);
  };

  const handleTimeAdjust = async (minutes: number) => {
    if (!timeBox) return;

    // 調整結束時間（加或減分鐘數）
    const newEndTime = new Date(timeBox.endTime);
    newEndTime.setMinutes(newEndTime.getMinutes() + minutes);

    await updateEndTime(newEndTime);
  };

  // ============================================
  // 渲染
  // ============================================

  // Loading 狀態
  if (isLoading && !timeBox) {
    return (
      <ResponsiveModal
        isOpen={isOpen}
        onClose={onClose}
        leftAction={
          <button
            className="text-gray-500 hover:text-gray-700"
            onClick={onClose}
            type="button"
          >
            <ArrowLeft size={20} />
          </button>
        }
      >
        <div className="flex items-center justify-center py-12">
          <div className="text-gray-500">Loading...</div>
        </div>
      </ResponsiveModal>
    );
  }

  // Error 狀態
  if (error || !timeBox) {
    return (
      <ResponsiveModal
        isOpen={isOpen}
        onClose={onClose}
        leftAction={
          <button
            className="text-gray-500 hover:text-gray-700"
            onClick={onClose}
            type="button"
          >
            <ArrowLeft size={20} />
          </button>
        }
      >
        <div className="flex items-center justify-center py-12">
          <div className="text-red-500">{error || 'Time box not found'}</div>
        </div>
      </ResponsiveModal>
    );
  }

  return (
    <ResponsiveModal
      isOpen={isOpen}
      onClose={onClose}
      title="TimeBox Details"
      leftAction={
        <button
          className="text-gray-500 hover:text-gray-700"
          onClick={onClose}
          type="button"
        >
          <ArrowLeft size={20} />
        </button>
      }
      saveButton={undefined}
    >
      <div className="flex flex-col h-full">
        {/* Main content area - scrollable */}
        <div className="flex-1 overflow-y-auto px-4 pt-4 pb-2">
          <div className="flex flex-col md:flex-row md:gap-6 min-h-full">
            {/* Content Area */}
            <TimeBoxDetailContent
              timeBox={timeBox}
              onUpdateTitle={updateTitle}
              onUpdateNotes={updateNotes}
              onUpdateTaskIds={updateTaskIds}
              onCategoryChange={updateCategory}
              aiRecommendations={aiRecommendations}
              isLoadingRecommendations={isLoadingRecommendations}
              recommendationsError={recommendationsError}
              onSelectRecommendation={handleSelectRecommendation}
              relatedTasks={relatedTasks}
              isLoadingRelatedTasks={isLoadingRelatedTasks}
            />

            {/* Metadata Sidebar (Desktop) */}
            <TimeBoxDetailMetadata
              timeBox={timeBox}
              onStartTimeChange={updateStartTime}
              onEndTimeChange={updateEndTime}
              onLocationChange={updateLocation}
              variant="desktop"
            />

            {/* Metadata Section (Mobile) */}
            <TimeBoxDetailMetadata
              timeBox={timeBox}
              onStartTimeChange={updateStartTime}
              onEndTimeChange={updateEndTime}
              onLocationChange={updateLocation}
              variant="mobile"
            />
          </div>
        </div>

        {/* Bottom action area */}
        <div className="shrink-0 border-t border-gray-100 px-4 py-3 sm:py-4 bg-white">
          <TimeBoxDetailActions
            mode={mode}
            onDelete={handleDelete}
            onTimeAdjust={handleTimeAdjust}
            onSwitchToEvent={onSwitchToEvent ? handleSwitchToEvent : undefined}
            isSubmitting={isSubmitting}
            startTime={timeBox.startTime}
            endTime={timeBox.endTime}
          />
          <div className="h-safe-bottom" />
        </div>
      </div>
    </ResponsiveModal>
  );
};

export default TimeBoxDetail;
